import React, { useEffect, useState } from "react";
import { Routes, Route, Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Landmark, CalendarDays, MapPin, FileText, Search, Download, ArrowRight, ArrowLeft, Gavel } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const pub = axios.create({ baseURL: API });

function fmtDate(d, t) {
  if (!d) return "";
  try {
    const base = new Date(`${d}T${t || "00:00"}`);
    const day = base.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
    return t ? `${day} at ${base.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : day;
  } catch { return d; }
}

function Shell({ children }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  return (
    <div className="min-h-screen bg-[#F7F9FB] text-[#172033]" data-testid="public-portal">
      <a href="#public-main" className="skip-link" data-testid="skip-to-main">Skip to main content</a>
      <header className="bg-[#123B63] text-white">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/public" className="flex items-center gap-2 font-bold text-lg">
            <Landmark className="h-6 w-6 text-[#C9A227]" /> GovFlowState
            <span className="hidden sm:inline text-[11px] font-medium uppercase tracking-widest text-[#8FB4D6]">Public Portal</span>
          </Link>
          <nav className="ml-auto flex items-center gap-4 text-sm">
            <Link to="/public/meetings" className="hover:text-[#C9A227]" data-testid="pub-nav-meetings">Meetings</Link>
            <Link to="/public/insights" className="hover:text-[#C9A227]" data-testid="pub-nav-items">Government Items</Link>
            <Link to="/public/search" className="hover:text-[#C9A227]" data-testid="pub-nav-search">Search</Link>
          </nav>
        </div>
      </header>
      <main id="public-main" className="max-w-5xl mx-auto px-4 py-6">{children}</main>
      <footer className="max-w-5xl mx-auto px-4 py-8 text-xs text-slate-400">
        Official government transparency portal. Documents shown are published public records.
      </footer>
    </div>
  );
}

function Card({ children, testid }) {
  return <div data-testid={testid} className="rounded-xl border border-[#DCE3EA] bg-white p-5 shadow-sm">{children}</div>;
}
function Empty({ title, sub }) {
  return <div className="text-center py-12"><p className="font-semibold text-[#172033]">{title}</p><p className="text-sm text-slate-500 mt-1">{sub}</p></div>;
}
function StatusPill({ children }) {
  return <span className="inline-flex rounded-full bg-[#E6F0EC] text-[#1F7A5B] border border-[#1F7A5B]/20 px-2.5 py-0.5 text-xs font-semibold">{children}</span>;
}

function MeetingRow({ m }) {
  return (
    <Link to={`/public/meetings/${m.meeting_id}`} data-testid="pub-meeting-card"
      className="block rounded-xl border border-[#DCE3EA] bg-white p-4 hover:border-[#3F7FB5] hover:shadow transition-all">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#3F7FB5]">{m.governing_body_name}</span>
        {m.has_agenda && <StatusPill>Agenda Published</StatusPill>}
      </div>
      <h3 className="font-bold text-lg mt-1">{m.title}</h3>
      <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {fmtDate(m.date, m.start_time)}</p>
      {m.location && <p className="text-sm text-slate-500 flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {m.location}</p>}
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#123B63] mt-2">View Agenda <ArrowRight className="h-4 w-4" /></span>
    </Link>
  );
}

function Home() {
  const [data, setData] = useState({ meetings: null });
  const nav = useNavigate();
  const [q, setQ] = useState("");
  useEffect(() => { pub.get("/meetings?scope=upcoming&limit=6").then((r) => setData({ meetings: r.data.meetings })).catch(() => setData({ meetings: [] })); }, []);
  return (
    <Shell>
      <div className="rounded-2xl bg-[#123B63] text-white p-8 mb-6">
        <h1 className="font-bold text-3xl">Government Transparency Portal</h1>
        <p className="text-[#B9D0E6] mt-2">Find upcoming meetings, agendas, decisions and public documents.</p>
        <form onSubmit={(e) => { e.preventDefault(); nav(`/public/search?q=${encodeURIComponent(q)}`); }} className="mt-4 flex gap-2 max-w-lg">
          <input data-testid="pub-home-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search meetings, resolutions, contracts…"
            className="flex-1 h-11 rounded-lg px-4 text-[#172033] outline-none" aria-label="Search government items" />
          <button className="rounded-lg bg-[#C9A227] px-5 font-semibold text-[#123B63]">Search</button>
        </form>
      </div>
      <h2 className="font-bold text-xl mb-3">Upcoming Meetings</h2>
      {data.meetings === null ? <p className="text-slate-400">Loading…</p>
        : data.meetings.length === 0 ? <Card><Empty title="No upcoming meetings" sub="There are currently no published upcoming meetings." /></Card>
        : <div className="grid gap-4 sm:grid-cols-2">{data.meetings.map((m) => <MeetingRow key={m.meeting_id} m={m} />)}</div>}
    </Shell>
  );
}

function Meetings() {
  const [scope, setScope] = useState("upcoming");
  const [meetings, setMeetings] = useState(null);
  useEffect(() => { setMeetings(null); pub.get(`/meetings?scope=${scope}&limit=100`).then((r) => setMeetings(r.data.meetings)).catch(() => setMeetings([])); }, [scope]);
  return (
    <Shell>
      <h1 className="font-bold text-2xl mb-3">Meetings</h1>
      <div className="flex gap-2 mb-4">
        {["upcoming", "past"].map((s) => (
          <button key={s} data-testid={`pub-scope-${s}`} onClick={() => setScope(s)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold border ${scope === s ? "bg-[#123B63] text-white border-[#123B63]" : "bg-white text-slate-600 border-[#DCE3EA]"}`}>
            {s === "upcoming" ? "Upcoming" : "Past"}
          </button>
        ))}
      </div>
      {meetings === null ? <p className="text-slate-400">Loading…</p>
        : meetings.length === 0 ? <Card><Empty title="No meetings" sub="No published meetings in this range." /></Card>
        : <div className="grid gap-4 sm:grid-cols-2">{meetings.map((m) => <MeetingRow key={m.meeting_id} m={m} />)}</div>}
    </Shell>
  );
}

function MeetingDetail() {
  const { id } = useParams();
  const [d, setD] = useState(undefined);
  useEffect(() => { pub.get(`/meetings/${id}`).then((r) => setD(r.data)).catch(() => setD(null)); }, [id]);
  if (d === undefined) return <Shell><p className="text-slate-400">Loading…</p></Shell>;
  if (d === null) return <Shell><Card><Empty title="Page Not Found" sub="This meeting is not available." /></Card></Shell>;
  const m = d.meeting;
  const outByNum = Object.fromEntries((d.outcomes || []).map((o) => [o.number, o]));
  return (
    <Shell>
      <Link to="/public/meetings" className="inline-flex items-center gap-1 text-sm text-slate-500 mb-3"><ArrowLeft className="h-4 w-4" /> All Meetings</Link>
      <Card testid="pub-meeting-detail">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#3F7FB5]">{m.governing_body_name}</span>
        <h1 className="font-bold text-2xl mt-1">{m.title}</h1>
        <p className="text-slate-600 mt-1 flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {fmtDate(m.date, m.start_time)}</p>
        {m.location && <p className="text-slate-500 flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {m.location}</p>}
        <div className="flex flex-wrap gap-2 mt-4">
          <a data-testid="pub-agenda-pdf" href={`${API}/meetings/${id}/agenda.pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#123B63] text-white px-4 py-2 text-sm font-semibold"><Download className="h-4 w-4" /> Download Agenda PDF</a>
          {m.status === "completed" && (
            <a data-testid="pub-summary-pdf" href={`${API}/meetings/${id}/summary.pdf`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#123B63] text-[#123B63] px-4 py-2 text-sm font-semibold"><Download className="h-4 w-4" /> Meeting Summary PDF</a>
          )}
          {d.version && <span className="text-xs text-slate-400 self-center">Publication version {d.version}</span>}
        </div>
        {d.quorum && (
          <div data-testid="pub-quorum" className="mt-4 border-t border-[#EEF2F6] pt-3 text-sm">
            <span className={`inline-flex items-center gap-1.5 font-semibold ${d.quorum.met ? "text-[#087F83]" : "text-slate-600"}`}>
              Quorum {d.quorum.met ? "Established" : "Not Established"}
            </span>
            {d.quorum.present_voting != null && <span className="text-slate-400"> · {d.quorum.present_voting} present · {d.quorum.required} required</span>}
          </div>
        )}
      </Card>
      {d.attendance && d.attendance.length > 0 && (
        <Card testid="pub-attendance" className="mt-6">
          <h2 className="font-bold text-lg mb-3">Attendance</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {d.attendance.map((a, i) => (
              <div key={i} data-testid="pub-attendance-row" className="flex items-center justify-between rounded-lg border border-[#EEF2F6] px-3 py-2 text-sm">
                <span className="text-[#172033]">{a.member_name}{a.role_label && <span className="text-slate-400"> · {a.role_label}</span>}</span>
                <span className={`font-semibold capitalize ${a.attendance_status === "present" ? "text-[#087F83]" : "text-slate-500"}`}>{a.attendance_status || "—"}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      <h2 className="font-bold text-xl mt-6 mb-3">Agenda</h2>
      {(!d.agenda || d.agenda.length === 0) ? <Card><Empty title="Agenda not yet available" sub="The published agenda will appear here." /></Card> : (
        <Card testid="pub-agenda">
          {Object.entries(d.agenda.reduce((acc, it) => { (acc[it.category_label] = acc[it.category_label] || []).push(it); return acc; }, {})).map(([cat, items]) => (
            <div key={cat} className="mb-4">
              <div className="text-sm font-bold uppercase tracking-wider text-[#3F7FB5] mb-1.5">{cat}</div>
              {items.map((it) => (
                <div key={it.number} data-testid="pub-agenda-item" className="flex items-start gap-3 py-1.5 border-b border-[#EEF2F6] last:border-0">
                  <span className="text-sm font-bold text-[#123B63] w-10">{it.number}</span>
                  <div className="flex-1">
                    <div className="font-medium">{it.title}</div>
                    {it.summary && <div className="text-sm text-slate-500">{it.summary}</div>}
                  </div>
                  {outByNum[it.number] && (
                    <div className="flex flex-col items-end gap-1" data-testid="pub-agenda-outcome">
                      <StatusPill>{outByNum[it.number].public_decision}</StatusPill>
                      {outByNum[it.number].vote_totals && (
                        <span className="text-xs text-slate-500" data-testid="pub-vote-totals">
                          YES {outByNum[it.number].vote_totals.YES || 0} · NO {outByNum[it.number].vote_totals.NO || 0} · WITHHELD {outByNum[it.number].vote_totals.WITHHELD || 0}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </Card>
      )}
    </Shell>
  );
}

function Insights() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const q = params.get("q") || "";
  useEffect(() => { setData(null); pub.get(`/insights?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}`).then((r) => setData(r.data)).catch(() => setData({ insights: [] })); }, [q]);
  return (
    <Shell>
      <h1 className="font-bold text-2xl mb-3">Government Items</h1>
      <input data-testid="pub-items-search" defaultValue={q} onBlur={(e) => setParams(e.target.value ? { q: e.target.value } : {})}
        onKeyDown={(e) => { if (e.key === "Enter") setParams(e.target.value ? { q: e.target.value } : {}); }}
        placeholder="Search government items…" aria-label="Search"
        className="w-full h-11 rounded-lg border border-[#DCE3EA] px-4 mb-4 outline-none focus:border-[#3F7FB5]" />
      {data === null ? <p className="text-slate-400">Loading…</p>
        : data.insights.length === 0 ? <Card><Empty title="No results found" sub="Try a different keyword." /></Card>
        : <div className="space-y-3">{data.insights.map((i) => (
            <Link key={i.insight_id} to={`/public/insights/${i.insight_id}`} data-testid="pub-item-row"
              className="block rounded-xl border border-[#DCE3EA] bg-white p-4 hover:border-[#3F7FB5] hover:shadow transition-all">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{i.title}</span><StatusPill>{i.public_status}</StatusPill>
              </div>
              {i.summary && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{i.summary}</p>}
            </Link>))}</div>}
    </Shell>
  );
}

function InsightDetail() {
  const { id } = useParams();
  const [d, setD] = useState(undefined);
  useEffect(() => { pub.get(`/insights/${id}`).then((r) => setD(r.data)).catch(() => setD(null)); }, [id]);
  if (d === undefined) return <Shell><p className="text-slate-400">Loading…</p></Shell>;
  if (d === null) return <Shell><Card><Empty title="Page Not Found" sub="This item is not available." /></Card></Shell>;
  return (
    <Shell>
      <Link to="/public/insights" className="inline-flex items-center gap-1 text-sm text-slate-500 mb-3"><ArrowLeft className="h-4 w-4" /> All Items</Link>
      <Card testid="pub-insight-detail">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#3F7FB5]">{d.insight_type_code}</span>
          <StatusPill>{d.public_status}</StatusPill>
        </div>
        <h1 className="font-bold text-2xl mt-1">{d.title}</h1>
        {d.summary && <p className="text-slate-700 mt-2">{d.summary}</p>}
        {d.upcoming?.length > 0 && (
          <div className="mt-4 rounded-lg bg-[#EAF1F8] border border-[#3F7FB5]/20 p-3">
            <div className="text-xs font-bold uppercase text-[#3F7FB5]">Scheduled</div>
            {d.upcoming.map((u) => <Link key={u.meeting_id} to={`/public/meetings/${u.meeting_id}`} className="block text-sm font-semibold text-[#123B63] hover:underline">{u.meeting_title} · {fmtDate(u.date)}</Link>)}
          </div>
        )}
      </Card>

      {Object.keys(d.content || {}).length > 0 && (
        <Card testid="pub-insight-content"><h2 className="font-bold mb-2">Details</h2>
          {Object.entries(d.content).map(([k, v]) => v && (
            <div key={k} className="mb-2"><div className="text-xs font-semibold uppercase text-slate-400 capitalize">{k.replace(/_/g, " ")}</div><p className="text-sm">{v}</p></div>
          ))}
        </Card>
      )}

      {d.attachments?.length > 0 && (
        <div className="mt-4"><h2 className="font-bold mb-2">Supporting Documents</h2>
          <div className="space-y-2">{d.attachments.map((a) => (
            <a key={a.attachment_id} data-testid="pub-attachment" href={`${API}/insights/${id}/attachments/${a.attachment_id}/download`} target="_blank" rel="noreferrer"
              className="flex items-center gap-3 rounded-lg border border-[#DCE3EA] bg-white px-3 py-2 hover:border-[#3F7FB5]">
              <FileText className="h-4 w-4 text-[#3F7FB5]" /><span className="flex-1 text-sm font-medium">{a.title}</span>
              <Download className="h-4 w-4 text-slate-400" /></a>))}</div>
        </div>
      )}

      {d.decision_history?.length > 0 && (
        <div className="mt-4"><h2 className="font-bold mb-2 flex items-center gap-2"><Gavel className="h-5 w-5 text-[#123B63]" /> Decision History</h2>
          <Card testid="pub-decision-history">
            <ol className="space-y-3">{d.decision_history.map((h, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-[#3F7FB5]" />
                <div><div className="font-semibold">{h.governing_body_name} <span className="text-slate-400 font-normal">· {fmtDate(h.meeting_date)}</span></div>
                  <StatusPill>{h.outcome_label}</StatusPill>
                  {h.vote_totals?.for != null && <span className="ml-2 text-sm text-slate-500">{h.vote_totals.for}–{h.vote_totals.against ?? 0}</span>}</div>
              </li>))}</ol>
          </Card>
        </div>
      )}
    </Shell>
  );
}

function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [res, setRes] = useState(null);
  const query = params.get("q") || "";
  useEffect(() => { if (!query) { setRes(null); return; } pub.get(`/search?q=${encodeURIComponent(query)}`).then((r) => setRes(r.data)).catch(() => setRes({ insights: [], meetings: [] })); }, [query]);
  return (
    <Shell>
      <h1 className="font-bold text-2xl mb-3">Search</h1>
      <form onSubmit={(e) => { e.preventDefault(); setParams(q ? { q } : {}); }} className="flex gap-2 mb-5 max-w-xl">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="pub-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search government items and meetings…" aria-label="Search"
            className="w-full h-11 rounded-lg border border-[#DCE3EA] pl-9 pr-3 outline-none focus:border-[#3F7FB5]" /></div>
        <button className="rounded-lg bg-[#123B63] text-white px-5 font-semibold">Search</button>
      </form>
      {res === null ? <p className="text-slate-400">Enter a search term above.</p>
        : (res.insights.length === 0 && res.meetings.length === 0) ? <Card><Empty title="No results found" sub="Try a different keyword or remove a filter." /></Card>
        : <div className="space-y-5">
            {res.meetings.length > 0 && <div><h2 className="font-bold mb-2">Meetings</h2><div className="grid gap-3 sm:grid-cols-2">{res.meetings.map((m) => <MeetingRow key={m.meeting_id} m={m} />)}</div></div>}
            {res.insights.length > 0 && <div><h2 className="font-bold mb-2">Government Items</h2><div className="space-y-2">{res.insights.map((i) => (
              <Link key={i.insight_id} to={`/public/insights/${i.insight_id}`} data-testid="pub-search-item" className="block rounded-lg border border-[#DCE3EA] bg-white p-3 hover:border-[#3F7FB5]">
                <span className="font-semibold">{i.title}</span></Link>))}</div></div>}
          </div>}
    </Shell>
  );
}

export default function PublicPortal() {
  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path="meetings" element={<Meetings />} />
      <Route path="meetings/:id" element={<MeetingDetail />} />
      <Route path="insights" element={<Insights />} />
      <Route path="insights/:id" element={<InsightDetail />} />
      <Route path="search" element={<SearchPage />} />
      <Route path="*" element={<Shell><Card><Empty title="Page Not Found" sub="The page you requested does not exist." /></Card></Shell>} />
    </Routes>
  );
}
